import { simpleApiService } from './simpleApiService'
import { useAuthStore } from '../store/authStore'
import { Paciente, Atencion, Gasto, Tratamiento } from '../types'

// Tipos para los resultados de búsqueda
export interface SearchResult {
  id: number
  type: 'paciente' | 'atencion' | 'gasto' | 'tratamiento'
  title: string
  subtitle?: string
  description?: string
  metadata?: Record<string, any>
}

export interface SearchResponse {
  pacientes: Paciente[]
  atenciones: Atencion[]
  gastos: Gasto[]
  tratamientos: Tratamiento[]
  total: number
}

class SearchService {
  private getToken(): string | undefined {
    return useAuthStore.getState().token
  }

  // Búsqueda unificada usando el endpoint global de búsqueda
  async searchAll(query: string, limit: number = 5): Promise<SearchResponse> {
    if (!query.trim()) {
      return {
        pacientes: [],
        atenciones: [],
        gastos: [],
        tratamientos: [],
        total: 0
      }
    }

    try {
      // Validar límite (máximo 50 según la API)
      const validLimit = Math.min(Math.max(1, limit), 50)
      
      const params = new URLSearchParams({
        q: query.trim(),
        limit: validLimit.toString()
      })

      // Llamar al endpoint unificado de búsqueda
      const response = await simpleApiService.get<{
        query: string
        resultados: {
          pacientes: Paciente[]
          atenciones: Atencion[]
          gastos: Gasto[]
          tratamientos: Tratamiento[]
          total_pacientes: number
          total_atenciones: number
          total_gastos: number
          total_tratamientos: number
          total_general: number
        }
      }>(`/search?${params.toString()}`, this.getToken())

      // Mapear la respuesta al formato esperado
      const resultados = response.resultados || {
        pacientes: [],
        atenciones: [],
        gastos: [],
        tratamientos: [],
        total_pacientes: 0,
        total_atenciones: 0,
        total_gastos: 0,
        total_tratamientos: 0,
        total_general: 0
      }

      return {
        pacientes: resultados.pacientes || [],
        atenciones: resultados.atenciones || [],
        gastos: resultados.gastos || [],
        tratamientos: resultados.tratamientos || [],
        total: resultados.total_general || 0
      }
    } catch (error) {
      console.error('Error en búsqueda unificada:', error)
      return {
        pacientes: [],
        atenciones: [],
        gastos: [],
        tratamientos: [],
        total: 0
      }
    }
  }

  // Convertir resultados a formato unificado para el dropdown
  formatSearchResults(searchResponse: SearchResponse): SearchResult[] {
    const results: SearchResult[] = []

    try {
      // Agregar pacientes
      searchResponse.pacientes.forEach(paciente => {
        results.push({
          id: paciente.id,
          type: 'paciente',
          title: paciente.nombre_completo,
          subtitle: `${paciente.tipo_documento}: ${paciente.numero_documento}`,
          description: paciente.telefono || paciente.email || paciente.sector,
          metadata: {
            saldo: paciente.saldo,
            telefono: paciente.telefono,
            email: paciente.email,
            sector: paciente.sector
          }
        })
      })

    // Agregar atenciones
    searchResponse.atenciones.forEach(atencion => {
      const valorTotal = atencion.valor_total 
        ? (typeof atencion.valor_total === 'string' 
            ? parseFloat(atencion.valor_total) 
            : atencion.valor_total)
        : 0
      
      results.push({
        id: atencion.id,
        type: 'atencion',
        title: atencion.nombre_completo || 'Sin nombre',
        subtitle: atencion.tratamiento || 'Sin tratamiento',
        description: `Fecha: ${atencion.fecha_hora ? new Date(atencion.fecha_hora).toLocaleDateString('es-ES') : 'Sin fecha'} - Valor: $${valorTotal.toFixed(2)}`,
        metadata: {
          fecha_hora: atencion.fecha_hora,
          valor_total: valorTotal,
          estado_pago: atencion.estado_pago,
          forma_pago: atencion.forma_pago,
          paciente_id: atencion.paciente_id
        }
      })
    })

    // Agregar gastos
    searchResponse.gastos.forEach(gasto => {
      const monto = gasto.monto 
        ? (typeof gasto.monto === 'string' 
            ? parseFloat(gasto.monto) 
            : gasto.monto)
        : 0
      
      results.push({
        id: gasto.id,
        type: 'gasto',
        title: gasto.concepto || 'Sin concepto',
        subtitle: gasto.categoria || 'Sin categoría',
        description: `${gasto.descripcion || 'Sin descripción'} - $${monto.toFixed(2)} ${gasto.moneda || 'USD'}`,
        metadata: {
          monto: monto,
          moneda: gasto.moneda,
          categoria: gasto.categoria,
          estado: gasto.estado,
          fecha_gasto: gasto.fecha_gasto
        }
      })
    })

    // Agregar tratamientos
    searchResponse.tratamientos.forEach(tratamiento => {
      const valor = tratamiento.valor 
        ? (typeof tratamiento.valor === 'string' 
            ? parseFloat(tratamiento.valor) 
            : tratamiento.valor)
        : 0
      
      results.push({
        id: tratamiento.id,
        type: 'tratamiento',
        title: tratamiento.nombre_tratamiento || 'Sin nombre',
        subtitle: tratamiento.codigo_interno || 'Sin código',
        description: `Valor: $${valor.toFixed(2)} - ${tratamiento.is_activo ? 'Activo' : 'Inactivo'}`,
        metadata: {
          valor: valor,
          is_activo: tratamiento.is_activo,
          detalles: tratamiento.detalles
        }
      })
    })

    return results
    } catch (error) {
      console.error('Error formateando resultados de búsqueda:', error)
      return []
    }
  }
}

export const searchService = new SearchService()
